import Konva from "konva";

import { getShapeConfig } from "~/telestrations/constants/shapes";
import { getKonvaCoordinatesFromSVG } from "~/telestrations/helpers/coordinateTransforms";
import { getOriginalPointCoord } from "~/telestrations/helpers/pointCoordinateHelper";
import { SpotlightShapeName } from "~/telestrations/stores/telestrationStore";
import { type Shape, ShapeType } from "~/telestrations/types/canvas";

import { parseSvgTextElement } from "./parseSvgTextElement";

interface ISVGTransform {
  translateX: number;
  translateY: number;
  rotate: number;
  scaleX: number;
  scaleY: number;
}

/**
 * Parses an SVG string and extracts shape configurations into an array of `Shape` objects.
 *
 * This function supports various SVG elements such as `circle`, `rect`, `text`, `polyline`,
 * `polygon`, `line`, and `path`. Each element is converted into a corresponding `Shape`
 * configuration object with its attributes and transformations applied.
 *
 * @param svgString - The SVG string to parse and extract shapes from.
 * @returns An array of `Shape` objects representing the parsed SVG elements.
 *
 * @remarks
 * - The function uses a `DOMParser` to parse the SVG string into an XML document.
 * - Unsupported SVG elements are logged as warnings in the console.
 * - Attributes such as `fill`, `stroke`, `stroke-width`, `transform`, and others are
 *   extracted and applied to the resulting shape configurations.
 * - Random IDs are generated for each shape, and the layer index is determined by the
 *   order of elements in the SVG string.
 *
 * @throws Will throw an error if the SVG string is invalid or cannot be parsed.
 *
 * @example
 * ```typescript
 * const svgString = `
 *   <svg>
 *     <circle cx="50" cy="50" r="40" fill="red" />
 *     <rect x="10" y="10" width="30" height="30" fill="blue" />
 *   </svg>
 * `;
 * const shapes = getShapesFromSVGString(svgString);
 * console.log(shapes);
 * ```
 */
export function getShapesFromSVGString(svgString: string): Shape[] {
  const shapes: Shape[] = [];
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
  const svgElements = svgDoc.querySelectorAll("ellipse, circle, rect, text, polyline, polygon, line, path, mask");
  let skipNextTextBackground = false;

  svgElements.forEach((svgElement) => {
    if (!svgElement) return;
    const shouldSkip = svgElement.getAttribute("skip") === "true";
    if (shouldSkip) return;
    const tagName = svgElement.tagName.toLowerCase();
    const id = String(Math.random());
    const layerIndex = shapes.length;

    const transform = parseSvgTransform(svgElement.getAttribute("transform"));
    const translateX = transform.translateX || 0;
    const translateY = transform.translateY || 0;
    const scaleX = transform.scaleX || 1;
    const scaleY = transform.scaleY || 1;
    const rotate = transform.rotate || 0;

    const fill = svgElement.getAttribute("fill") || Konva.Util.getRandomColor();
    const rotation = parseFloat(svgElement.getAttribute("rotation") || "0");
    const stroke = svgElement.getAttribute("stroke") || "black";
    // Restore original stroke-width by multiplying by scale factor (inverse of compensation during save)
    const svgStrokeWidth = parseFloat(svgElement.getAttribute("stroke-width") || "1");
    const avgScale = (scaleX + scaleY) / 2;
    const strokeWidth = svgStrokeWidth * avgScale;
    const dash =
      svgElement
        .getAttribute("stroke-dasharray")
        ?.split(",")
        .map((d) => parseFloat(d) || 0) || [];

    // handle text background rect
    if (tagName === "rect" && svgElement.getAttribute("data-text-background") === "true") {
      const nextText = svgElement.nextElementSibling;
      if (nextText?.tagName.toLowerCase() !== "text") {
        console.warn(`Could not find text element for background rect: ${svgElement.outerHTML}`);
        return;
      }

      const fill = nextText.getAttribute("fill") || "black";
      const background = svgElement.getAttribute("fill") || "transparent";
      const textShape = parseSvgTextElement(nextText, fill, layerIndex, background);
      if (textShape) {
        shapes.push(textShape);
      }

      skipNextTextBackground = true;
      return;
    }

    if (skipNextTextBackground && tagName === "text") {
      skipNextTextBackground = false;
      return;
    }

    const cx = parseFloat(svgElement.getAttribute("cx") || "0");
    const cy = parseFloat(svgElement.getAttribute("cy") || "0");
    const svgX = parseFloat(svgElement.getAttribute("x") || "0");
    const svgY = parseFloat(svgElement.getAttribute("y") || "0");
    const pointToBeTransformed = tagName === "circle" || tagName === "ellipse" ? { x: cx, y: cy } : { x: svgX, y: svgY };
    // Apply the inverse transformation to get the Konva coordinates
    const { x, y } = getKonvaCoordinatesFromSVG({ ...pointToBeTransformed, rotation: rotate, scaleX, scaleY });

    switch (tagName) {
      case "circle": {
        const width = parseFloat(svgElement.getAttribute("width") || "1");
        const height = parseFloat(svgElement.getAttribute("height") || "1");
        const radius = parseFloat(svgElement.getAttribute("r") || "0");
        const shouldSkip = svgElement.getAttribute("skip") === "true";
        const name = svgElement.nodeName || SpotlightShapeName.ELLIPSE;
        if (shouldSkip) return;

        if (Number.isNaN(cx) || Number.isNaN(cy) || Number.isNaN(radius)) {
          console.warn(`Invalid attributes for circle element: ${svgElement.outerHTML}`);
          return;
        }

        const isInsideMask = svgElement.parentNode && (svgElement.parentNode as Element).tagName.toLowerCase() === "mask";
        const spotLightParams = isInsideMask ? { width, height, fill: "transparent", name, strokeWidth: 0 } : {};
        shapes.push(
          getShapeConfig(
            {
              id,
              type: isInsideMask ? ShapeType.Spotlight : ShapeType.Ellipse,
              startX: x,
              startY: y,
              layerIndex,
            },
            {
              strokeWidth,
              stroke,
              radiusX: radius,
              radiusY: radius,
              fill,
              scaleX,
              scaleY,
              dash,
              x: x - radius,
              y: y - radius,
              ...spotLightParams,
            }
          )
        );
        break;
      }
      case "rect": {
        const width = parseFloat(svgElement.getAttribute("width") || "0");
        const height = parseFloat(svgElement.getAttribute("height") || "0");

        if (Number.isNaN(x) || Number.isNaN(y) || Number.isNaN(width) || Number.isNaN(height)) {
          console.warn(`Invalid attributes for rect element: ${svgElement.outerHTML}`);
          return;
        }
        const isInsideMask = svgElement.parentNode && (svgElement.parentNode as Element).tagName.toLowerCase() === "mask";
        shapes.push(
          getShapeConfig(
            {
              id,
              type: isInsideMask ? ShapeType.Spotlight : ShapeType.Rectangle,
              startX: x,
              startY: y,
              layerIndex,
            },
            {
              width,
              height,
              strokeWidth: isInsideMask ? 0 : strokeWidth,
              stroke,
              x,
              y,
              fill: isInsideMask ? "transparent" : fill,
              rotation: rotation || rotate,
              scaleX,
              scaleY,
              dash,
              name: SpotlightShapeName.RECTANGLE,
            }
          )
        );
        break;
      }
      case "text": {
        const textShape = parseSvgTextElement(svgElement, fill, layerIndex);
        if (textShape) {
          shapes.push(textShape);
        }
        break;
      }
      case "polyline":
      case "polygon": {
        const pointsString = svgElement.getAttribute("points");
        if (!pointsString) {
          console.warn(`Missing points attribute for ${tagName} element: ${svgElement.outerHTML}`);
          return;
        }

        const points = pointsString.split(" ").flatMap((point) => {
          const coords = point.split(",");
          return coords.length === 2 ? [parseFloat(coords[0]), parseFloat(coords[1])] : null;
        });

        if (points.includes(null)) {
          console.warn(`Invalid points attribute for ${tagName} element: ${svgElement.outerHTML}`);
          return;
        }

        shapes.push(addLine(svgElement, layerIndex, translateX, translateY, strokeWidth, stroke, { translateX, translateY, scaleX, scaleY, rotate }));
        break;
      }
      case "path": {
        const d = svgElement.getAttribute("d");
        if (!d) {
          console.warn(`Missing d attribute for path element: ${svgElement.outerHTML}`);
          return;
        }

        const points = d.split(/[ML,]/).filter(Boolean).map(parseFloat);
        if (points.some(Number.isNaN)) {
          console.warn(`Invalid d attribute for path element: ${svgElement.outerHTML}`);
          return;
        }

        const originalPoints = points.map((point: number, index: number) => {
          const isX = index % 2 === 0;
          const offset = isX ? translateX || 0 : translateY || 0;
          const scale = isX ? scaleX || 1 : scaleY || 1;
          return point - offset / scale;
        });

        shapes.push(
          getShapeConfig(
            {
              id,
              layerIndex,
              type: ShapeType.Arrow,
              startX: translateX,
              startY: translateY,
            },
            {
              points: originalPoints,
              x: translateX,
              y: translateY,
              scale: { x: scaleX, y: scaleY },
              rotation: rotate || 0,
              strokeWidth,
              stroke,
              dash,
            }
          )
        );
        break;
      }
      case "ellipse": {
        const rx = parseFloat(svgElement.getAttribute("rx") || "0");
        const ry = parseFloat(svgElement.getAttribute("ry") || "0");
        if (Number.isNaN(cx) || Number.isNaN(cy) || Number.isNaN(rx) || Number.isNaN(ry)) {
          console.warn(`Invalid attributes for ellipse element: ${svgElement.outerHTML}`);
          return;
        }
        const isInsideMask = svgElement.parentNode && (svgElement.parentNode as Element).tagName.toLowerCase() === "mask";
        shapes.push(
          getShapeConfig(
            {
              id,
              type: isInsideMask ? ShapeType.Spotlight : ShapeType.Ellipse,
              startX: x,
              startY: y,
              layerIndex,
            },
            {
              strokeWidth: isInsideMask ? 0 : strokeWidth,
              stroke,
              radiusX: rx,
              radiusY: ry,
              fill: isInsideMask ? "transparent" : fill,
              scaleX,
              scaleY,
              dash,
              x,
              y,
            }
          )
        );
        break;
      }
      default:
        console.warn(`Unsupported SVG element: ${tagName}`);
    }
  });

  return shapes;
}

function parseSvgTransform(transformString: string | null): ISVGTransform {
  const transformations: ISVGTransform = {} as ISVGTransform;

  if (!transformString) {
    return transformations;
  }

  // Extract translate
  const translateMatch = transformString.match(/translate\((-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)\)/);
  if (translateMatch) {
    transformations.translateX = parseFloat(translateMatch[1]);
    transformations.translateY = parseFloat(translateMatch[3]);
  }

  // Extract rotate
  const rotateMatch = transformString.match(/rotate\((-?\d+(\.\d+)?)(?:,\s*(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?))?\)/);
  if (rotateMatch) {
    transformations.rotate = parseFloat(rotateMatch[1]);
    // If center coordinates are provided, use them as translate
    if (rotateMatch[3] && rotateMatch[5]) {
      transformations.translateX = parseFloat(rotateMatch[3]);
      transformations.translateY = parseFloat(rotateMatch[5]);
    }
  }

  // Extract scale
  const scaleMatch = transformString.match(/scale\((-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)\)/);
  if (scaleMatch) {
    transformations.scaleX = parseFloat(scaleMatch[1]);
    transformations.scaleY = parseFloat(scaleMatch[3]);
  } else {
    const singleScaleMatch = transformString.match(/scale\((-?\d+(\.\d+)?)\)/);
    if (singleScaleMatch) {
      transformations.scaleX = parseFloat(singleScaleMatch[1]);
      transformations.scaleY = parseFloat(singleScaleMatch[1]);
    }
  }

  return transformations;
}

function addLine(
  svgElement: Element,
  layerIndex: number,
  startX: number,
  startY: number,
  strokeWidth: number,
  stroke: string,
  { translateX, translateY, scaleX, scaleY, rotate }: ISVGTransform
): Shape {
  const pointsString = svgElement.getAttribute("points") as string;

  // Parse points and apply inverse scaling to get original coordinates
  const points: number[] = [];
  const pointPairs = pointsString.split(" ").map((point) => {
    const coords = point.split(",");
    return [parseFloat(coords[0]), parseFloat(coords[1])];
  });

  for (let i = 0; i < pointPairs.length; i++) {
    const [x, y] = pointPairs[i];
    points.push(getOriginalPointCoord(x, scaleX, translateX));
    points.push(getOriginalPointCoord(y, scaleY, translateY));
  }

  const shape = getShapeConfig(
    {
      id: svgElement.getAttribute("id") as string,
      layerIndex,
      type: ShapeType.Line,
      startX,
      startY,
    },
    {
      x: translateX,
      y: translateY,
      points,
      scale: { x: scaleX || 1, y: scaleY || 1 },
      rotation: rotate || 0,
      strokeWidth,
      stroke,
    }
  );
  return shape;
}
